/**
 * Centralized country flag utilities
 * Complete worldwide coverage using ISO 3166-1 alpha-2 codes
 */

import { getCountryCallingCode, CountryCode } from 'libphonenumber-js';

// Complete list of ISO 3166-1 alpha-2 country codes
export const ALL_ISO2_CODES: CountryCode[] = [
  'AC', 'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS',
  'BT', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN', 'CO',
  'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE', 'EG',
  'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF', 'GG',
  'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GT', 'GU', 'GW', 'GY', 'HK', 'HN', 'HR', 'HT',
  'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM', 'JO', 'JP', 'KE',
  'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR',
  'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN',
  'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF',
  'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL',
  'PM', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW', 'SA', 'SB', 'SC', 'SD',
  'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SX', 'SY',
  'SZ', 'TA', 'TC', 'TD', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW',
  'TZ', 'UA', 'UG', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI', 'VN', 'VU', 'WF', 'WS', 'XK',
  'YE', 'YT', 'ZA', 'ZM', 'ZW'
];

// Build dial code to ISO2 mapping dynamically from libphonenumber-js
const dialCodeToIso2Map: Map<string, string[]> = new Map();

// Initialize the dial code mapping
ALL_ISO2_CODES.forEach((iso2) => {
  try {
    const dialCode = getCountryCallingCode(iso2);
    if (dialCode) {
      const existing = dialCodeToIso2Map.get(dialCode) || [];
      existing.push(iso2);
      dialCodeToIso2Map.set(dialCode, existing);
    }
  } catch {
    // Some codes like 'AC' may not have calling codes
  }
});

// Country name aliases for common variations and misspellings
const countryNameAliases: Record<string, string> = {
  // Official name variations
  'USA': 'US',
  'United States of America': 'US',
  'United States': 'US',
  'America': 'US',
  'UK': 'GB',
  'United Kingdom': 'GB',
  'Great Britain': 'GB',
  'Britain': 'GB',
  'England': 'GB',
  'UAE': 'AE',
  'United Arab Emirates': 'AE',
  'Emirates': 'AE',
  'Russia': 'RU',
  'Russian Federation': 'RU',
  'South Korea': 'KR',
  'Korea South': 'KR',
  'Republic of Korea': 'KR',
  'Korea': 'KR',
  'North Korea': 'KP',
  'DPRK': 'KP',
  'China': 'CN',
  "People's Republic of China": 'CN',
  'PRC': 'CN',
  'Taiwan': 'TW',
  'Republic of China': 'TW',
  
  // African countries
  'DR Congo': 'CD',
  'DRC': 'CD',
  'Democratic Republic of Congo': 'CD',
  'Democratic Republic of the Congo': 'CD',
  'Congo-Kinshasa': 'CD',
  'Congo Kinshasa': 'CD',
  'Zaire': 'CD',
  'Congo': 'CG',
  'Republic of Congo': 'CG',
  'Congo-Brazzaville': 'CG',
  'Congo Brazzaville': 'CG',
  'Ivory Coast': 'CI',
  "Côte d'Ivoire": 'CI',
  'Cote dIvoire': 'CI',
  'South Africa': 'ZA',
  'RSA': 'ZA',
  'South Sudan': 'SS',
  'Sudan South': 'SS',
  'Eswatini': 'SZ',
  'Swaziland': 'SZ',
  'Cabo Verde': 'CV',
  'Cape Verde': 'CV',
  'Tanzania': 'TZ',
  'United Republic of Tanzania': 'TZ',
  'Réunion': 'RE',
  'Reunion': 'RE',
  'São Tomé and Príncipe': 'ST',
  'Sao Tome and Principe': 'ST',
  'Sao Tome': 'ST',
  'Guinea-Bissau': 'GW',
  'Guinea Bissau': 'GW',
  'Equatorial Guinea': 'GQ',
  'Central African Republic': 'CF',
  'CAR': 'CF',
  
  // Middle East
  'Palestine': 'PS',
  'State of Palestine': 'PS',
  'Palestinian Territories': 'PS',
  'Syria': 'SY',
  'Syrian Arab Republic': 'SY',
  'Iran': 'IR',
  'Islamic Republic of Iran': 'IR',
  'Persia': 'IR',
  
  // Europe
  'Czech Republic': 'CZ',
  'Czechia': 'CZ',
  'Slovakia': 'SK',
  'Slovak Republic': 'SK',
  'Macedonia': 'MK',
  'North Macedonia': 'MK',
  'FYROM': 'MK',
  'Bosnia': 'BA',
  'Bosnia and Herzegovina': 'BA',
  'Bosnia-Herzegovina': 'BA',
  'Netherlands': 'NL',
  'Holland': 'NL',
  'The Netherlands': 'NL',
  'Vatican': 'VA',
  'Vatican City': 'VA',
  'Holy See': 'VA',
  'Moldova': 'MD',
  'Republic of Moldova': 'MD',
  
  // Asia
  'Hong Kong': 'HK',
  'Hong Kong SAR': 'HK',
  'Macau': 'MO',
  'Macao': 'MO',
  'Macau SAR': 'MO',
  'Myanmar': 'MM',
  'Burma': 'MM',
  'Laos': 'LA',
  "Lao People's Democratic Republic": 'LA',
  'Lao PDR': 'LA',
  'Vietnam': 'VN',
  'Viet Nam': 'VN',
  'Brunei': 'BN',
  'Brunei Darussalam': 'BN',
  'East Timor': 'TL',
  'Timor-Leste': 'TL',
  'Timor Leste': 'TL',
  
  // Americas
  'Venezuela': 'VE',
  'Bolivarian Republic of Venezuela': 'VE',
  'Bolivia': 'BO',
  'Plurinational State of Bolivia': 'BO',
  'Saint Vincent': 'VC',
  'Saint Vincent and the Grenadines': 'VC',
  'St Vincent': 'VC',
  'Saint Kitts': 'KN',
  'Saint Kitts and Nevis': 'KN',
  'St Kitts': 'KN',
  'Saint Lucia': 'LC',
  'St Lucia': 'LC',
  'Trinidad': 'TT',
  'Trinidad and Tobago': 'TT',
  'Antigua': 'AG',
  'Antigua and Barbuda': 'AG',
  'US Virgin Islands': 'VI',
  'USVI': 'VI',
  'British Virgin Islands': 'VG',
  'BVI': 'VG',
  'Turks and Caicos': 'TC',
  'Turks and Caicos Islands': 'TC',
  'Cayman': 'KY',
  'Cayman Islands': 'KY',
  'French Guiana': 'GF',
  'Guiana': 'GF',
  'Puerto Rico': 'PR',
  
  // Oceania
  'Micronesia': 'FM',
  'Federated States of Micronesia': 'FM',
  'FSM': 'FM',
  'Papua New Guinea': 'PG',
  'PNG': 'PG',
  'Marshall Islands': 'MH',
  'Solomon Islands': 'SB',
  'French Polynesia': 'PF',
  'Tahiti': 'PF',
  'New Caledonia': 'NC',
  'Wallis and Futuna': 'WF',
  'Wallis': 'WF',
  'Cook Islands': 'CK',
  'Pitcairn': 'PN',
  'Pitcairn Islands': 'PN',
  'Norfolk Island': 'NF',
  'Christmas Island': 'CX',
  'Cocos Islands': 'CC',
  'Cocos (Keeling) Islands': 'CC',
  
  // Other territories
  'Falkland Islands': 'FK',
  'Falklands': 'FK',
  'Faroe Islands': 'FO',
  'Faroes': 'FO',
  'Greenland': 'GL',
  'Gibraltar': 'GI',
  'Bermuda': 'BM',
  'Jersey': 'JE',
  'Guernsey': 'GG',
  'Isle of Man': 'IM',
  'Martinique': 'MQ',
  'Guadeloupe': 'GP',
  'Mayotte': 'YT',
  'Saint Pierre': 'PM',
  'Saint Pierre and Miquelon': 'PM',
  'Saint Helena': 'SH',
  'Ascension': 'AC',
  'Ascension Island': 'AC',
  'Tristan da Cunha': 'SH',
  'Svalbard': 'SJ',
  'Svalbard and Jan Mayen': 'SJ',
  'Kosovo': 'XK',
  'Aland Islands': 'AX',
  'Åland Islands': 'AX',
  'Curacao': 'CW',
  'Curaçao': 'CW',
  'Sint Maarten': 'SX',
  'Saint Martin': 'MF',
  'Saint Barthelemy': 'BL',
  'Saint-Barthélemy': 'BL',
  'Caribbean Netherlands': 'BQ',
  'Bonaire': 'BQ',
  'Aruba': 'AW',
  'Montserrat': 'MS',
  'Anguilla': 'AI',
  'American Samoa': 'AS',
  'Guam': 'GU',
  'Northern Mariana Islands': 'MP',
  'Palau': 'PW',
  'Nauru': 'NR',
  'Tuvalu': 'TV',
  'Tokelau': 'TK',
  'Niue': 'NU',
};

/**
 * Convert ISO 3166-1 alpha-2 code to flag emoji
 */
export function iso2ToFlagEmoji(iso2: string): string {
  if (!iso2 || iso2.length !== 2) return '🌍';
  
  const codePoints = iso2
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  
  return String.fromCodePoint(...codePoints);
}

/**
 * Get ISO2 code from dial code
 * Returns the first matching country for ambiguous codes
 */
export function getIso2FromDialCode(dialCode: string): string | undefined {
  if (!dialCode) return undefined;
  
  const cleanCode = dialCode.replace(/^\+/, '').trim();
  const countries = dialCodeToIso2Map.get(cleanCode);
  
  return countries?.[0];
}

/**
 * Get ISO2 code from country name (handles aliases and variations)
 */
export function getIso2FromCountryName(countryName: string): string | undefined {
  if (!countryName) return undefined;
  
  const normalized = countryName.trim();
  
  // Check aliases first (exact match)
  if (countryNameAliases[normalized]) {
    return countryNameAliases[normalized];
  }
  
  // Check case-insensitive aliases
  const lowerName = normalized.toLowerCase();
  for (const [alias, iso2] of Object.entries(countryNameAliases)) {
    if (alias.toLowerCase() === lowerName) {
      return iso2;
    }
  }
  
  // Try to use Intl.DisplayNames to match official names
  try {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    
    for (const iso2 of ALL_ISO2_CODES) {
      const officialName = displayNames.of(iso2);
      if (officialName && officialName.toLowerCase() === lowerName) {
        return iso2;
      }
    }
    
    // Try partial matching for official names
    for (const iso2 of ALL_ISO2_CODES) {
      const officialName = displayNames.of(iso2);
      if (officialName && officialName.toLowerCase().includes(lowerName)) {
        return iso2;
      }
    }
  } catch {
    // Intl.DisplayNames not supported
  }
  
  return undefined;
}

/**
 * Get flag emoji from dial code (e.g., "254" -> "🇰🇪")
 */
export function getFlagFromDialCode(dialCode: string | null | undefined): string {
  if (!dialCode) return '🌍';
  
  const iso2 = getIso2FromDialCode(dialCode);
  if (iso2) {
    return iso2ToFlagEmoji(iso2);
  }
  
  return '🌍';
}

/**
 * Get flag emoji from country name (e.g., "Kenya" -> "🇰🇪")
 */
export function getFlagFromCountryName(countryName: string | null | undefined): string {
  if (!countryName) return '🌍';
  
  const iso2 = getIso2FromCountryName(countryName);
  if (iso2) {
    return iso2ToFlagEmoji(iso2);
  }
  
  return '🌍';
}

/**
 * Get a flag SVG URL.
 * Uses FlagCDN (ISO2-based, fast, deterministic) when possible, with a
 * pinned Twemoji fallback for non-country emoji. Pinned version avoids
 * the `@latest` tag which can be flaky on iOS WKWebView cold-start.
 */
const TWEMOJI_BASE = 'https://cdn.jsdelivr.net/gh/jdecked/twemoji@15.1.0/assets/svg';
const FLAGCDN_BASE = 'https://flagcdn.com';

export function getFlagSvgUrl(flagEmoji: string): string {
  if (!flagEmoji || flagEmoji === '🌍') {
    return `${TWEMOJI_BASE}/1f30d.svg`;
  }

  // Try to extract ISO2 from the regional indicator code points (much faster path)
  const cps = [...flagEmoji].map(c => c.codePointAt(0)).filter((c): c is number => !!c);
  if (cps.length === 2 && cps.every(cp => cp >= 0x1f1e6 && cp <= 0x1f1ff)) {
    const iso2 = cps.map(cp => String.fromCharCode((cp - 0x1f1e6) + 65)).join('').toLowerCase();
    return `${FLAGCDN_BASE}/${iso2}.svg`;
  }

  const codePoints = cps.map(cp => cp.toString(16).toLowerCase());
  if (codePoints.length === 0) return `${TWEMOJI_BASE}/1f30d.svg`;
  return `${TWEMOJI_BASE}/${codePoints.join('-')}.svg`;
}

/**
 * Get dial code to ISO2 mapping
 */
export function getDialCodeToIso2(dialCode: string): string | undefined {
  return getIso2FromDialCode(dialCode);
}

/**
 * Flag component props for rendering with Twemoji
 */
export interface FlagImageProps {
  dialCode?: string | null;
  countryName?: string | null;
  flagEmoji?: string | null;
  size?: number;
  className?: string;
}

/**
 * Get the flag emoji - tries dial code first, then country name, then uses provided emoji
 * This function ALWAYS returns a valid flag emoji for any country worldwide
 */
export function getFlag(options: {
  dialCode?: string | null;
  countryName?: string | null;
  flagEmoji?: string | null;
}): string {
  const { dialCode, countryName, flagEmoji } = options;

  // 1. Country name first - most specific (dial "1" alone is ambiguous across
  //    24 NANP countries and resolves to AG alphabetically, producing the wrong
  //    flag for US/CA/etc).
  if (countryName) {
    const flag = getFlagFromCountryName(countryName);
    if (flag !== '🌍') return flag;
  }

  // 2. Use provided emoji if it's a valid regional-indicator flag
  if (flagEmoji && flagEmoji !== '🌍' && flagEmoji.length >= 2) {
    const codePoint = flagEmoji.codePointAt(0);
    if (codePoint && codePoint >= 0x1F1E6 && codePoint <= 0x1F1FF) {
      return flagEmoji;
    }
  }

  // 3. Dial code last - ambiguous for NANP and other shared codes
  if (dialCode) {
    const flag = getFlagFromDialCode(dialCode);
    if (flag !== '🌍') return flag;
  }

  return '🌍';
}

/**
 * Get all countries for a given dial code
 * Useful for ambiguous codes like +1 (US, Canada, Caribbean)
 */
export function getCountriesForDialCode(dialCode: string): string[] {
  const cleanCode = dialCode.replace(/^\+/, '').trim();
  return dialCodeToIso2Map.get(cleanCode) || [];
}

/**
 * Validate if a string is a valid ISO2 country code
 */
export function isValidIso2(code: string): boolean {
  return ALL_ISO2_CODES.includes(code.toUpperCase() as CountryCode);
}
